import { NoSsr } from "@mui/base";
import { useSetAtom } from "jotai";
import type { FunctionComponent } from "react";

import { IdleQueue } from "~/components/common/idle-queue";
import { Spacer } from "~/components/common/spacer";
import { hamburgerMenuAtom } from "~/components/hamburger-menu/atoms";
import { DefaultHeaderSwapThreshold } from "~/components/header/consts";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import { useEditionValue } from "~/lib/edition";
import { useGetHomeAsPath } from "~/lib/router/hooks";
import { page as HomePage } from "~/pages/home";

import IconMenu from "./header-icon/menu.svg";
import { useHeader, useSwapOnScrollEffect } from "./hooks";
import {
  ActionButton,
  Container,
  StyledDynamicHeaderOnelineMenuContainer,
  StyledLogoFlag,
  StyledLogoLink,
  StyledLogoText,
} from "./styles";
import type { HeaderConfiguration } from "./types";

export type Props = {
  className?: string;
  headerConfiguration?: HeaderConfiguration;
};

export const Header: FunctionComponent<Props> = ({ className, headerConfiguration }) => {
  const edition = useEditionValue();
  const homeAsPath = useGetHomeAsPath();

  const setHamburgerMenuState = useSetAtom(hamburgerMenuAtom);

  const { shouldSwap } = useSwapOnScrollEffect(DefaultHeaderSwapThreshold);
  const { mastheadQueryParameter, renderFeatures, responsiveLogoVariant } = useHeader(
    shouldSwap,
    headerConfiguration,
  );

  const { pathname: homePagePathname, query: homePageQuery } = HomePage.route({
    edition,
    slide: "main",
  });

  return (
    <Container className={className}>
      <StyledLogoLink
        $responsiveVariants={responsiveLogoVariant}
        anchorProps={{ "aria-label": "SCMP homepage" }}
        as={homeAsPath}
        pathname={homePagePathname}
        query={{ ...homePageQuery, ...mastheadQueryParameter }}
      >
        <StyledLogoFlag />
        <StyledLogoText />
      </StyledLogoLink>
      <NoSsr>
        <StyledDynamicHeaderOnelineMenuContainer
          appBarVariant={headerConfiguration?.appBarVariant}
        />
      </NoSsr>
      <Spacer />
      {renderFeatures()}
      <IdleQueue>
        <ActionButton
          $responsiveVariants={{
            desktopUp: "hidden",
            largeDesktopUp: "hidden",
            mobileUp: "show",
            tabletUp: "show",
          }}
          onClick={() => {
            setHamburgerMenuState(current => ({
              ...current,
              isOpen: !current.isOpen,
              variant: "default",
            }));
            sendGATracking({
              action: "Menu/Open",
              category: "Menu",
              label: "Menu/Open",
            });
            sendGA4Tracking({
              action: "click",
              category: "menu",
              customized_parameters: {
                action_type: "open",
                edition,
                menu_type: "hamburger",
              },
              subcategory: "section",
            });
          }}
        >
          <IconMenu />
        </ActionButton>
      </IdleQueue>
    </Container>
  );
};

Header.displayName = "Header";
